<template>
  <!-- Employee self-service view into the own personnel file (Stage 3,
       docs/product/people/personnel-file.md) and the one way into it: the
       employee hands a document in (issue 976). An employee without a linked
       Person, without a people role or without the module sees no card at all
       — the workspace stays quiet instead of apologizing — but a reachable file
       shows the card even when it is empty, because that is where the first
       hand-in starts.

       A chip row (workspace-start M3), same visual family as the module bands
       and the other tail rows: label = the document's title, click = the same
       download the row's button used to trigger. A hand-in carries ONE state
       line under its chip (awaiting HR / accepted); a hand-in HR sent back
       shows as an outcome line with the reason until it is dismissed or the
       employee hands in again. HR-uploaded documents are the file itself and
       carry no state line. -->
  <section
    v-if="available"
    data-testid="my-documents"
    class="flex flex-col gap-3 rounded-xl border bg-surface p-5 shadow-sm"
  >
    <div class="flex flex-wrap items-center justify-between gap-2">
      <h2 class="flex items-center gap-2 text-base font-semibold text-heading">
        <LumiIcon name="file" class="h-5 w-5 text-tertiary" />
        {{ t('homebase.myDocuments.title') }}
      </h2>
      <div class="flex items-center gap-2">
        <Button
          v-if="documents.length > PREVIEW_COUNT"
          variant="link"
          @click="expanded = !expanded"
        >
          {{ expanded ? t('homebase.myDocuments.showLess') : t('homebase.myDocuments.showAll', { count: documents.length }) }}
        </Button>
        <Button
          v-if="canHandIn && !panelOpen"
          variant="secondary"
          size="sm"
          data-testid="hand-in-open"
          @click="openPanel()"
        >
          {{ t('homebase.myDocuments.handIn') }}
        </Button>
      </div>
    </div>

    <EmptyState
      v-if="documents.length === 0 && returns.length === 0 && !panelOpen"
      quiet
      class="my-documents-empty"
      :title="t('homebase.myDocuments.empty')"
      data-testid="my-documents-empty"
    />

    <form
      v-if="canHandIn && panelOpen"
      class="flex flex-col gap-3 rounded-lg border border-accent-ring bg-surface-wash p-4"
      data-testid="hand-in-panel"
      @submit.prevent="submit"
    >
      <div class="flex flex-col gap-2">
        <span class="text-sm font-medium text-heading">{{ t('homebase.myDocuments.kindLabel') }}</span>
        <ChoiceGroup
          v-model="category"
          variant="tiles"
          :options="kindOptions"
          :label="t('homebase.myDocuments.kindLabel')"
        />
      </div>
      <label class="flex flex-col gap-1 text-sm font-medium text-heading">
        {{ t('homebase.myDocuments.fileLabel') }}
        <!-- accept is a convenience for the picker (a phone offers the camera),
             never the check: people-api sniffs the bytes. -->
        <input
          ref="picker"
          type="file"
          accept="image/*,application/pdf"
          class="text-sm font-normal text-body"
          data-testid="hand-in-file"
          @change="onPick"
        />
      </label>
      <InlineNotice v-if="handInError" variant="error" data-testid="hand-in-error">{{ handInError }}</InlineNotice>
      <div class="flex justify-end gap-2">
        <Button variant="ghost" :disabled="sending" @click="closePanel">{{ t('homebase.myDocuments.cancel') }}</Button>
        <Button type="submit" :loading="sending" data-testid="hand-in-send">{{ t('homebase.myDocuments.send') }}</Button>
      </div>
    </form>

    <ul v-if="returns.length > 0" class="flex flex-col gap-2" role="list" data-testid="my-documents-returns">
      <li
        v-for="ret in returns"
        :key="ret.id"
        class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-body"
        data-testid="my-documents-return"
      >
        <span class="min-w-0 flex-1">
          {{ t('homebase.myDocuments.sentBack', { date: shortDate(ret.returnedAt), reason: ret.reason }) }}
        </span>
        <Button v-if="canHandIn" variant="link" @click="openPanel(ret.category)">
          {{ t('homebase.myDocuments.handInAgain') }}
        </Button>
        <Button variant="ghost" size="sm" data-testid="my-documents-return-dismiss" @click="dismissReturn(ret)">
          {{ t('homebase.myDocuments.dismiss') }}
        </Button>
      </li>
    </ul>

    <ul v-if="documents.length > 0" class="flex flex-col gap-2" role="list">
      <li v-for="doc in visibleDocuments" :key="doc.id" class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          type="button"
          class="my-documents-chip"
          :title="chipTitle(doc)"
          @click="download(doc)"
        >
          <LumiIcon name="download" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span class="truncate">{{ doc.fileName }}</span>
        </button>
        <span v-if="stateLine(doc)" class="text-xs text-secondary" data-testid="my-documents-state" :data-status="doc.status">{{ stateLine(doc) }}</span>
      </li>
    </ul>
    <InlineNotice v-if="downloadError" variant="error">{{ t('homebase.myDocuments.downloadFailed') }}</InlineNotice>
  </section>
</template>

<script setup lang="ts">
import { useApiMutation } from '@lumi/app-kit';
import { ChoiceGroup, EmptyState, InlineNotice, Button, LumiIcon } from '@lumi/ui';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { peopleApi } from '@/utils/api';

type OwnDocument = {
  id: string;
  category: string;
  fileName: string;
  documentDate: string | null;
  createdAt: string;
  // Hand-in state (issue 976). HR-uploaded documents have no handedInAt.
  status?: 'pending' | 'accepted';
  handedInAt?: string | null;
  acceptedAt?: string | null;
};

type OwnReturn = {
  id: string;
  category: string;
  fileName: string;
  reason: string;
  returnedAt: string;
};

// What an employee can hand in — the same five kinds people-api accepts.
const HAND_IN_KINDS = ['certificate', 'attestation', 'health_certificate', 'identification', 'other'] as const;
const PREVIEW_COUNT = 5;

const { t, locale } = useI18n();
const documents = ref<OwnDocument[]>([]);
const returns = ref<OwnReturn[]>([]);
// The own list answered: the file is reachable (also with zero documents).
const available = ref(false);
// A 403 on the hand-in itself means this role may read but not hand in.
const canHandIn = ref(true);
const expanded = ref(false);
const downloadError = ref(false);
const panelOpen = ref(false);
const category = ref<string>('');
const file = ref<File | null>(null);
const picker = ref<HTMLInputElement | null>(null);
const handInError = ref('');

const kindOptions = computed(() =>
  HAND_IN_KINDS.map((kind) => ({ id: kind, label: t(`homebase.myDocuments.category.${kind}`), testid: `hand-in-kind-${kind}` })),
);

const visibleDocuments = computed(() =>
  expanded.value ? documents.value : documents.value.slice(0, PREVIEW_COUNT),
);

function formatDate(value: string | null | undefined) {
  return value ? String(value).slice(0, 10) : '';
}

// "22 Oct" in the reader's language.
function shortDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDate(value);
  return new Intl.DateTimeFormat(String(locale.value), { day: 'numeric', month: 'short' }).format(date);
}

function chipTitle(doc: OwnDocument): string {
  const category = t(`homebase.myDocuments.category.${doc.category}`);
  const date = formatDate(doc.documentDate || doc.createdAt);
  return date ? `${category} · ${date}` : category;
}

function stateLine(doc: OwnDocument): string {
  if (!doc.handedInAt) return '';
  const date = shortDate(doc.handedInAt);
  return doc.status === 'pending'
    ? t('homebase.myDocuments.handedIn', { date })
    : t('homebase.myDocuments.accepted', { date });
}

async function load() {
  try {
    // suppressGlobalError: absence of the module/entitlement/role degrades to
    // "no card", the homebase fan-out convention.
    const res = await peopleApi.get('/persons/me/documents', { suppressGlobalError: true } as any);
    documents.value = Array.isArray(res.data) ? res.data : [];
    available.value = true;
  } catch {
    documents.value = [];
    available.value = false;
    return;
  }
  try {
    const res = await peopleApi.get('/persons/me/documents/returns', { suppressGlobalError: true } as any);
    returns.value = Array.isArray(res.data) ? res.data : [];
  } catch {
    returns.value = [];
  }
}

async function download(doc: OwnDocument) {
  downloadError.value = false;
  try {
    const res = await peopleApi.get(`/persons/me/documents/${doc.id}/content`, { suppressGlobalError: true } as any);
    if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
  } catch {
    downloadError.value = true;
  }
}

function openPanel(preselect = '') {
  handInError.value = '';
  category.value = (HAND_IN_KINDS as readonly string[]).includes(preselect) ? preselect : '';
  file.value = null;
  panelOpen.value = true;
}

function closePanel() {
  panelOpen.value = false;
  file.value = null;
  category.value = '';
  handInError.value = '';
}

function onPick(event: Event) {
  file.value = (event.target as HTMLInputElement).files?.[0] ?? null;
}

// One plain sentence per code people-api answers with; anything else gets the
// generic one. privacy_unavailable and hand_in_not_possible read the same.
function errorMessage(code: unknown): string {
  const key =
    code === 'hand_in_not_possible' || code === 'privacy_unavailable'
      ? 'not_possible'
      : ['file_refused', 'file_too_large', 'file_empty', 'file_missing', 'quota_exceeded', 'invalid_category', 'scan_unavailable'].includes(String(code))
        ? String(code)
        : 'generic';
  return t(`homebase.myDocuments.error.${key}`);
}

// The write goes through the mutation seam. No toast on success — the document appearing in
// the list, with its state line, is the confirmation.
// CONSUMER (submit): reads outcome.ok / status / code; the user sees one plain sentence per code
// in the hand-in panel (handInError), or — on a 403 — the control goes away.
const handInMutation = useApiMutation({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (form: FormData) => {
    await peopleApi.post('/persons/me/documents/hand-in', form, { suppressGlobalError: true } as any);
  },
});
const sending = handInMutation.pending;

async function submit() {
  handInError.value = '';
  if (!category.value) {
    handInError.value = errorMessage('invalid_category');
    return;
  }
  if (!file.value) {
    handInError.value = errorMessage('file_missing');
    return;
  }
  const form = new FormData();
  form.append('category', category.value);
  form.append('file', file.value);
  const outcome = await handInMutation.mutate(form);
  if (outcome.ok) {
    closePanel();
    // The new document is in the list at once; the server has deleted any
    // sent-back outcome, so the reload drops those lines too.
    await load();
    return;
  }
  if (outcome.status === 403) {
    // This role may read its file but not hand in: take the control away.
    canHandIn.value = false;
    closePanel();
    return;
  }
  handInError.value = errorMessage(outcome.code);
}

// Dismissing is a write like any other: a failure reports on the app-wide channel and the
// line stays, so dismissing again is harmless.
const dismissMutation = useApiMutation({
  tier: 'inlineEdit',
  run: async (ret: OwnReturn) => {
    await peopleApi.post(`/persons/me/documents/returns/${ret.id}/dismiss`, undefined, { suppressGlobalError: true } as any);
  },
  onSuccess: (_result, ret) => {
    returns.value = returns.value.filter((r) => r.id !== ret.id);
  },
});

async function dismissReturn(ret: OwnReturn) {
  await dismissMutation.mutate(ret);
}

onMounted(load);
</script>

<style scoped>
/* A neutral chip (workspace-start M3), same family as the other tail rows'
   pills: wash ground, pill ring, truncates a long file name rather than
   wrapping the row. */
.my-documents-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 220px;
  padding: 4px 10px;
  border-radius: var(--radius-pill, 999px);
  border: 1px solid var(--pill-ring, var(--border-default));
  background: var(--surface-wash);
  color: var(--text-body);
  font-size: var(--text-sm);
  text-align: left;
  transition: background-color var(--dur-fast, 150ms) ease;
}
.my-documents-chip:hover {
  background: var(--accent-soft-2, var(--surface-wash));
}
.my-documents-chip:focus-visible {
  outline: 2px solid var(--accent-ring);
  outline-offset: 1px;
}
/* The empty line sits inside a card, not on a blank page. */
.my-documents-empty {
  padding: var(--space-2) 0;
}
</style>
